import { getPrimaryRole } from "./accessList";
import { readMandateSession } from "./mandateSessionStorage";

/** Roles that always have full module access. */
const ALWAYS_FULL_ACCESS_ROLES = new Set([
  "ROLE_ADMIN",
  "ROLE_CLINIC",
  "ROLE_CLINICSUPERADMIN",
]);

/** ROLE_CLINIC (and ROLE_ADMIN) always have full access. */
export const hasAlwaysFullAccess = () =>
  ALWAYS_FULL_ACCESS_ROLES.has(getPrimaryRole());

/**
 * Resolve the clinic role display name for permission matching
 * (e.g. "Veterinarian" vs "Paravet/Nurse" for ROLE_DOCTOR).
 */
export const getCurrentUserClinicRoleName = () => {
  const session = readMandateSession();
  if (
    session?.currentUserClinicRole &&
    typeof session.currentUserClinicRole === "string"
  ) {
    return session.currentUserClinicRole;
  }

  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    for (const field of ["role", "roleName", "clinicRole"]) {
      const value = user?.[field];
      if (value && typeof value === "string" && !value.startsWith("ROLE_")) {
        return value;
      }
    }

    const userId = user?.doctorId ?? user?.id ?? localStorage.getItem("userId");
    const vets = Array.isArray(session?.vets) ? session.vets : [];
    if (userId && vets.length > 0) {
      const vet = vets.find((v) =>
        [v?.doctorId, v?.id, v?.userId].some(
          (id) => id != null && String(id) === String(userId),
        ),
      );
      return vet?.role ?? vet?.roleName ?? null;
    }
  } catch {
    // ignore
  }

  return null;
};

/**
 * Modules map for the logged-in user from mandate roleModulePermissions.
 * Prefer ROLE_* authority; use clinic role name only to disambiguate duplicates
 * (e.g. Veterinarian vs Paravet/Nurse for ROLE_DOCTOR).
 */
export const getCurrentUserModules = () => {
  if (hasAlwaysFullAccess()) return null;

  const session = readMandateSession();
  const permissions = Array.isArray(
    session?.roleModulePermissions?.rolePermissions,
  )
    ? session.roleModulePermissions.rolePermissions
    : [];

  if (!permissions.length) return null;

  const authority = getPrimaryRole();
  const clinicRoleName = getCurrentUserClinicRoleName();
  const matches = authority
    ? permissions.filter((item) => item?.role === authority)
    : [];

  if (matches.length === 1) return matches[0]?.modules ?? null;

  if (matches.length > 1) {
    if (clinicRoleName) {
      const byName = matches.find((item) => item?.name === clinicRoleName);
      if (byName?.modules) return byName.modules;
    }
    const preferred =
      matches.find((item) => item?.name === "Veterinarian") || matches[0];
    return preferred?.modules ?? null;
  }

  if (clinicRoleName) {
    const byName = permissions.find((item) => item?.name === clinicRoleName);
    if (byName?.modules) return byName.modules;
  }

  return null;
};

/**
 * Access level for a module: "none" | "read" | "full".
 * Defaults to "full" when permissions are missing or role is unrestricted.
 */
export const getModuleAccess = (moduleKey) => {
  if (!moduleKey) return "full";
  // ROLE_CLINIC / ROLE_ADMIN always have full access
  if (hasAlwaysFullAccess()) return "full";

  const modules = getCurrentUserModules();
  if (!modules) return "full";

  const access = modules[moduleKey];
  if (access == null || access === "") return "full";
  return access;
};

/** True when the user can create / edit / delete for the module. */
export const canWriteModule = (moduleKey) =>
  hasAlwaysFullAccess() || getModuleAccess(moduleKey) === "full";

/** True when the user can at least view the module. */
export const canReadModule = (moduleKey) => {
  if (hasAlwaysFullAccess()) return true;
  const access = getModuleAccess(moduleKey);
  return access === "read" || access === "full";
};
